import { useMemo } from "react";
import moment from "moment";
import {
  BookableRefInventory,
  Insurance,
  InsuranceCalculationBase,
  InsuranceTaxation,
  Rate,
  RateConstant,
  RatePercentage,
  Restrictions,
  Translation,
  Typology,
  TypologyAmount,
  TypologyPercentage,
  TypologySection,
} from "../../../gen/witbooking/backoffice/insurance/v1/insurance_api_pb";
import { usePropertyLocales } from "../../../components/property-locale-provider";
import {
  BookableRefInventoryEntity,
  InsuranceCalculationBaseEnum,
  InsuranceEntity,
  InsuranceTaxationEntity,
  RestrictionsEntity,
  TypologyEntity,
  TypologyEnum,
  TypologySectionEntity,
} from "../entity";

const mapSectionList = (section: TypologySection.AsObject): TypologySectionEntity => {
  return {
    amountFrom:
      (section.amountFrom?.percentage
        ? section.amountFrom.percentage.value
        : section.amountFrom?.constant?.value) || 0,
    amountTo:
      (section.amountTo?.percentage
        ? section.amountTo.percentage.value
        : section.amountTo?.constant?.value) || 0,
    value:
      (section.value?.percentage
        ? section.value.percentage.value
        : section.value?.constant?.value) || 0,
  };
};

const mapRestrictions = (restrictions?: Restrictions.AsObject): RestrictionsEntity => {
  return {
    minStay: restrictions?.minStay?.value ? restrictions.minStay.value / 24 : undefined,
    maxStay: restrictions?.maxStay?.value ? restrictions.maxStay.value / 24 : undefined,
    minNotice: restrictions?.minNotice?.value,
    maxNotice: restrictions?.maxNotice?.value,
  };
};

const mapInventoryLine = (
  bookableRef?: BookableRefInventory.AsObject
): BookableRefInventoryEntity => {
  return {
    tickersList: bookableRef?.tickersList || [],
  };
};

const mapTranslations = (
  translationsList: Translation.AsObject[],
  allowedLocales: string[]
): any[] => {
  return translationsList
    .filter(translation => allowedLocales.includes(translation.locale))
    .map(translation => ({
      locale: translation.locale,
      name: translation.name,
      description: translation.description,
    }));
};

const mapTypology = (typology?: Typology.AsObject): TypologyEntity => {
  if (typology?.percentage) {
    return {
      percentage: { sectionsList: typology.percentage?.sectionsList.map(mapSectionList) || [] },
    };
  }
  if (typology?.amount) {
    return { amount: { sectionsList: typology.amount?.sectionsList.map(mapSectionList) || [] } };
  }
  throw new Error("Unknown typology");
};

const mapTaxation = (taxationList: InsuranceTaxation.AsObject[]): InsuranceTaxationEntity[] => {
  return taxationList.map(taxation => ({
    id: taxation.id,
    ticker: taxation.ticker,
  }));
};

// @ts-ignore
const mapTimestamp = (timestamp?: proto.google.protobuf.Timestamp): Date | undefined => {
  if (!timestamp || (timestamp.seconds === 0 && timestamp.nanos === 0)) {
    return undefined;
  }
  return new Date(timestamp.seconds * 1000 + timestamp.nanos / 1000000);
};

export function useMapper() {
  const allowedLocales = usePropertyLocales();

  return useMemo(
    () => ({
      toObject: (insurance: Insurance): InsuranceEntity => {
        const props = insurance.toObject();
        return InsuranceEntity.of({
          id: props.id,
          type: props.typology?.amount ? TypologyEnum.AMOUNT : TypologyEnum.PERCENTAGE,
          calculationBase:
            props.calculationBase === InsuranceCalculationBase.ACCOMMODATION_ONLY
              ? InsuranceCalculationBaseEnum.ACCOMMODATION_ONLY
              : InsuranceCalculationBaseEnum.ACCOMMODATION_AND_EXTRAS,
          ticker: props.ticker,
          active: props.active,
          typology: mapTypology(props.typology),
          validFrom: mapTimestamp(props.validFrom),
          validTo: mapTimestamp(props.validTo),
          restrictions: mapRestrictions(props.restrictions),
          appliesToInventories: mapInventoryLine(props.appliesToInventories),
          taxes: mapTaxation(props.taxesList),
          fees: mapTaxation(props.feesList),
          createdAt: mapTimestamp(props.createdAt),
          updatedAt: mapTimestamp(props.updatedAt),
          translationsList: mapTranslations(props.translationsList, allowedLocales),
        });
      },
      toGrpc: (props: InsuranceEntity): Insurance => {
        const insurance = new Insurance();
        insurance.setId(props.id);
        insurance.setTicker(props.ticker);
        insurance.setActive(props.active);
        insurance.setCalculationBase(
          props.calculationBase === InsuranceCalculationBaseEnum.ACCOMMODATION_ONLY
            ? InsuranceCalculationBase.ACCOMMODATION_ONLY
            : InsuranceCalculationBase.ACCOMMODATION_AND_EXTRAS
        );

        // Taxes
        if (props.taxes.length > 0) {
          insurance.setTaxesList(
            props.taxes.map(it => {
              const taxation = new InsuranceTaxation();
              taxation.setId(it.id);
              taxation.setTicker(it.ticker);
              return taxation;
            })
          );
        }

        // Fees
        if (props.fees.length > 0) {
          insurance.setFeesList(
            props.fees.map(it => {
              const taxation = new InsuranceTaxation();
              taxation.setId(it.id);
              taxation.setTicker(it.ticker);
              return taxation;
            })
          );
        }

        if (props.validFrom) {
          // @ts-ignore
          const validFrom = new proto.google.protobuf.Timestamp();
          validFrom.setSeconds(moment(props.validFrom).unix());
          validFrom.setNanos(0);
          insurance.setValidFrom(validFrom);
        }

        if (props.validTo) {
          // @ts-ignore
          const validTo = new proto.google.protobuf.Timestamp();
          validTo.setSeconds(moment(props.validTo).unix());
          validTo.setNanos(0);
          insurance.setValidTo(validTo);
        }

        // Constant Rate
        const mapConstantAmount = (amount: number) => {
          const rateConstant = new RateConstant();
          rateConstant.setValue(amount);
          const rate = new Rate();
          rate.setConstant(rateConstant);
          return rate;
        };

        // Percentage Rate
        const mapPercentageAmount = (amount: number) => {
          const ratePercentage = new RatePercentage();
          ratePercentage.setValue(Number(amount));
          const rate = new Rate();
          rate.setPercentage(ratePercentage);
          return rate;
        };

        // Typology
        const typology = new Typology();
        if (props.typology.percentage) {
          const percentageTypology = new TypologyPercentage();
          percentageTypology.setSectionsList(
            props.typology.percentage.sectionsList.map(section => {
              const grpcSection = new TypologySection();
              grpcSection.setAmountFrom(mapConstantAmount(section.amountFrom));
              grpcSection.setAmountTo(mapConstantAmount(section.amountTo));
              grpcSection.setValue(mapPercentageAmount(section.value));
              return grpcSection;
            })
          );
          typology.setPercentage(percentageTypology);
        } else if (props.typology.amount) {
          const amountTypology = new TypologyAmount();
          amountTypology.setSectionsList(
            props.typology.amount.sectionsList.map(section => {
              const grpcSection = new TypologySection();
              grpcSection.setAmountFrom(mapConstantAmount(section.amountFrom));
              grpcSection.setAmountTo(mapConstantAmount(section.amountTo));
              grpcSection.setValue(mapConstantAmount(section.value));
              return grpcSection;
            })
          );
          typology.setAmount(amountTypology);
        }
        insurance.setTypology(typology);

        // Restrictions
        if (props.restrictions) {
          const restrictions = new Restrictions();

          if (props.restrictions.minStay) {
            const minStay = new RateConstant();
            minStay.setValue(props.restrictions.minStay * 24);
            restrictions.setMinStay(minStay);
          }
          if (props.restrictions.maxStay) {
            const maxStay = new RateConstant();
            maxStay.setValue(props.restrictions.maxStay * 24);
            restrictions.setMaxStay(maxStay);
          }
          if (props.restrictions.minNotice) {
            const minNotice = new RateConstant();
            minNotice.setValue(props.restrictions.minNotice);
            restrictions.setMinNotice(minNotice);
          }
          if (props.restrictions.maxNotice) {
            const maxNotice = new RateConstant();
            maxNotice.setValue(props.restrictions.maxNotice);
            restrictions.setMaxNotice(maxNotice);
          }
          insurance.setRestrictions(restrictions);
        }

        // Inventories
        const bookableRefInventory = new BookableRefInventory();
        bookableRefInventory.setTickersList(props.appliesToInventories.tickersList);
        insurance.setAppliesToInventories(bookableRefInventory);

        // Translations
        insurance.setTranslationsList(
          props.translationsList.map(translation => {
            const grpcTranslation = new Translation();
            grpcTranslation.setLocale(translation.locale);
            grpcTranslation.setName(translation.name);
            grpcTranslation.setDescription(translation.description);
            return grpcTranslation;
          })
        );

        return insurance;
      },
    }),
    [allowedLocales]
  );
}
